/**
 * Front camera access with the quirks of Safari iOS and Chrome Android handled:
 *  - the <video> must be muted + playsinline + autoplay, and play() must be
 *    called from a user gesture on iOS (the "Try-On" button click);
 *  - OverconstrainedError → retry with relaxed constraints;
 *  - the stream resolution can change (orientation, applyConstraints): the
 *    `resize` event on the video is forwarded so intrinsics are recomputed.
 */
export type CameraErrorCode = 'denied' | 'no_device' | 'insecure_context' | 'in_use' | 'unknown';

export class CameraError extends Error {
  constructor(
    readonly code: CameraErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'CameraError';
  }
}

export interface CameraOptions {
  /** Ideal capture size on the long side (the browser may give less). */
  idealLongSide: number;
  idealFrameRate: number;
  facingMode: 'user' | 'environment';
}

export const DEFAULT_CAMERA_OPTIONS: CameraOptions = { idealLongSide: 1280, idealFrameRate: 30, facingMode: 'user' };

export class CameraController {
  readonly video: HTMLVideoElement;
  private stream: MediaStream | null = null;
  private generation = 0;
  private resizeListeners: Array<(w: number, h: number) => void> = [];

  constructor(video: HTMLVideoElement) {
    this.video = video;
    video.muted = true;
    video.autoplay = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    video.addEventListener('resize', () => {
      const { videoWidth: w, videoHeight: h } = video;
      if (w > 0 && h > 0) for (const l of this.resizeListeners) l(w, h);
    });
  }

  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function';
  }

  async start(options: Partial<CameraOptions> = {}): Promise<{ width: number; height: number }> {
    this.stop();
    const generation = this.generation;
    const opts = { ...DEFAULT_CAMERA_OPTIONS, ...options };
    if (!window.isSecureContext) throw new CameraError('insecure_context', 'Camera requires HTTPS (or localhost).');
    if (!CameraController.isSupported()) throw new CameraError('no_device', 'getUserMedia not available.');

    const attempts: MediaStreamConstraints[] = [
      {
        video: {
          facingMode: opts.facingMode,
          width: { ideal: opts.idealLongSide },
          height: { ideal: Math.round((opts.idealLongSide * 9) / 16) },
          frameRate: { ideal: opts.idealFrameRate },
        },
        audio: false,
      },
      { video: { facingMode: opts.facingMode }, audio: false },
      { video: true, audio: false },
    ];

    let lastErr: unknown = null;
    for (const constraints of attempts) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (generation !== this.generation) {
          stream.getTracks().forEach((track) => track.stop());
          throw new DOMException("Camera start cancelled", "AbortError");
        }
        this.stream = stream;
        break;
      } catch (err) {
        if (generation !== this.generation) throw err;
        lastErr = err;
        const name = (err as DOMException).name;
        if (name === 'NotAllowedError' || name === 'SecurityError') throw new CameraError('denied', 'Camera permission denied.');
        if (name === 'NotReadableError' || name === 'AbortError') throw new CameraError('in_use', 'Camera is in use by another app.');
        // NotFoundError / OverconstrainedError: try relaxed constraints.
      }
    }
    if (!this.stream) {
      const name = (lastErr as DOMException | undefined)?.name;
      throw new CameraError(name === 'NotFoundError' ? 'no_device' : 'unknown', String(lastErr));
    }

    this.video.srcObject = this.stream;
    try {
      await this.video.play();
      await this.waitForDimensions();
      if (generation !== this.generation) throw new DOMException("Camera start cancelled", "AbortError");
    } catch (err) {
      if (generation === this.generation) this.stop();
      throw err;
    }
    return { width: this.video.videoWidth, height: this.video.videoHeight };
  }

  private waitForDimensions(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.video.videoWidth > 0) return resolve();
      const onMeta = () => {
        if (this.video.videoWidth > 0) {
          cleanup();
          resolve();
        }
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new CameraError('unknown', 'Video never reported dimensions.'));
      }, 10_000);
      const cleanup = () => {
        clearTimeout(timer);
        this.video.removeEventListener('loadedmetadata', onMeta);
        this.video.removeEventListener('resize', onMeta);
      };
      this.video.addEventListener('loadedmetadata', onMeta);
      this.video.addEventListener('resize', onMeta);
    });
  }

  onResize(listener: (w: number, h: number) => void): () => void {
    this.resizeListeners.push(listener);
    return () => {
      this.resizeListeners = this.resizeListeners.filter((l) => l !== listener);
    };
  }

  /** Current track settings (resolution / frame rate as delivered by the browser). */
  getSettings(): MediaTrackSettings | null {
    const track = this.stream?.getVideoTracks()[0];
    return track ? track.getSettings() : null;
  }

  /**
   * Requests a different capture resolution without restarting the stream.
   * Chrome Android honours it; Safari often ignores it (we then rely on the
   * inference-side downscale in FrameGrabber instead).
   */
  async applyLongSide(longSide: number): Promise<boolean> {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return false;
    try {
      await track.applyConstraints({ width: { ideal: longSide }, height: { ideal: Math.round((longSide * 9) / 16) } });
      return true;
    } catch {
      return false;
    }
  }

  get isRunning(): boolean {
    return !!this.stream && this.stream.active;
  }

  stop(): void {
    this.generation++;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.video.srcObject = null;
  }
}
